// ===========================================
// POST-SHIFT PAGE - REPORT & CLOCK OUT
// ===========================================

import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useAuthStore } from '../stores/authStore';
import { useShiftStore } from '../stores/shiftStore';
import { shiftApi } from '../services/api';
import {
  Clock,
  DollarSign,
  Users,
  TrendingUp,
  TrendingDown,
  ChevronRight,
  ChevronLeft,
  Check,
  CheckCircle,
  Loader2,
  AlertTriangle,
  MessageSquare,
  LogOut,
  Star,
  Utensils,
  Wrench,
  HelpCircle,
  X,
  Plus,
} from 'lucide-react';
import clsx from 'clsx';

type PostShiftStep = 'summary' | 'tables' | 'tips' | 'notes' | 'clockout';

const STEPS: { id: PostShiftStep; label: string }[] = [
  { id: 'summary', label: 'Summary' },
  { id: 'tables', label: 'Tables' },
  { id: 'tips', label: 'Tips' },
  { id: 'notes', label: 'Notes' },
  { id: 'clockout', label: 'Clock Out' },
];

const ISSUE_CATEGORIES = [
  { id: 'food', label: 'Food', icon: Utensils, color: 'amber' },
  { id: 'service', label: 'Service', icon: Users, color: 'blue' },
  { id: 'equipment', label: 'Equipment', icon: Wrench, color: 'slate' },
  { id: 'other', label: 'Other', icon: HelpCircle, color: 'purple' },
];

export default function PostShiftPage() {
  const navigate = useNavigate();
  const { employee, logout } = useAuthStore();
  const {
    postShiftNotes,
    postShiftIssues,
    setPostShiftNotes,
    addPostShiftIssue,
    removePostShiftIssue,
    resetShift,
  } = useShiftStore();

  const [currentStep, setCurrentStep] = useState<PostShiftStep>('summary');
  const contentRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [newIssue, setNewIssue] = useState({ category: '', description: '', severity: 'medium' });
  const [showIssueForm, setShowIssueForm] = useState(false);
  const [cashTipsDeclared, setCashTipsDeclared] = useState(0);

  // Fetch post-shift summary
  const { data: summaryData, isLoading, isError, error: queryError, refetch } = useQuery({
    queryKey: ['post-shift-summary', employee?.id],
    queryFn: async () => {
      if (!employee?.id) throw new Error('No employee');
      const response = await shiftApi.getPostShiftSummary(employee.id);
      if (!response.success) throw new Error(response.error?.message || 'Failed to load');
      return response.data;
    },
    enabled: !!employee?.id,
    staleTime: 0,
    retry: 1,
  });

  // Submit report mutation
  const submitReportMutation = useMutation({
    mutationFn: async () => {
      if (!employee?.id) throw new Error('No employee');
      const response = await shiftApi.submitPostShiftReport(employee.id, {
        notes: postShiftNotes || undefined,
        issues: postShiftIssues.length > 0 ? postShiftIssues : undefined,
        cashTipsDeclaredCents: Math.round(cashTipsDeclared * 100),
      });
      if (!response.success) throw new Error(response.error?.message || 'Failed to submit');
      return response.data;
    },
  });

  // Clock out mutation
  const clockOutMutation = useMutation({
    mutationFn: async () => {
      if (!employee?.id) throw new Error('No employee');
      // Submit the report first — but if the shift was ALREADY closed
      // (auto clock-out, manager clock-out, double-tap), don't trap the
      // employee here: their goal is to finish, so continue gracefully.
      try {
        await submitReportMutation.mutateAsync();
      } catch (e: any) {
        if (!/no active shift/i.test(e?.message || '')) throw e;
      }
      // Then clock out
      const response = await shiftApi.clockOut(employee.id);
      if (!response.success) {
        if (/not clocked in/i.test(response.error?.message || '')) {
          // Already clocked out — treat as success and exit calmly.
          return { alreadyClockedOut: true };
        }
        throw new Error(response.error?.message || 'Failed to clock out');
      }
      return response.data;
    },
    onSuccess: () => {
      resetShift();
      logout();
      navigate('/');
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const handleNext = () => {
    const stepIndex = STEPS.findIndex(s => s.id === currentStep);
    if (stepIndex < STEPS.length - 1) {
      setCurrentStep(STEPS[stepIndex + 1].id);
    }
  };

  const handleBack = () => {
    const stepIndex = STEPS.findIndex(s => s.id === currentStep);
    if (stepIndex > 0) {
      setCurrentStep(STEPS[stepIndex - 1].id);
    }
  };

  const handleAddIssue = () => {
    if (newIssue.category && newIssue.description.trim()) {
      addPostShiftIssue({
        category: newIssue.category,
        description: newIssue.description.trim(),
        severity: newIssue.severity,
      });
      setNewIssue({ category: '', description: '', severity: 'medium' });
      setShowIssueForm(false);
    }
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}m`;
    return `${hours}h ${mins}m`;
  };

  const formatCurrency = (cents: number) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const currentStepIndex = STEPS.findIndex(s => s.id === currentStep);

  // Reset the content scroll to top on step change AND once the summary data
  // loads, so a shorter step (e.g. Table Breakdown) never appears clipped at
  // the top from an inherited scroll position.
  useLayoutEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [currentStep, summaryData]);

  // Force reset handler for stuck shifts
  const handleForceReset = () => {
    resetShift();
    logout();
    navigate('/');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary-600" />
          <p className="text-slate-600">Loading shift summary...</p>
        </div>
      </div>
    );
  }

  // Shift already closed (auto clock-out, manager clock-out) — that's not an
  // error, the person is simply done. One calm button out.
  if (isError && /no active shift/i.test((queryError as Error)?.message || '')) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-6 shadow-lg max-w-sm w-full text-center">
          <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900 mb-2">You're all clocked out</h2>
          <p className="text-slate-600 mb-6">
            This shift was already closed — nothing more to do here. Have a good one!
          </p>
          <button
            onClick={handleForceReset}
            className="w-full py-3 bg-primary-600 text-white rounded-xl font-semibold"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  // Handle error state - show force reset option
  if (isError || !summaryData) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-6 shadow-lg max-w-sm w-full text-center">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900 mb-2">Shift Data Unavailable</h2>
          <p className="text-slate-600 mb-6">
            {(queryError as Error)?.message || 'Unable to load your shift summary. Your shift may have already ended or there was a system issue.'}
          </p>
          <div className="space-y-3">
            <button
              onClick={() => refetch()}
              className="w-full py-3 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200"
            >
              Try Again
            </button>
            <button
              onClick={handleForceReset}
              className="w-full py-3 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600"
            >
              Reset & Return to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Apply defaults to handle incomplete API responses
  const summary = summaryData ? {
    shift: {
      clockIn: summaryData.shift?.clockIn || new Date().toISOString(),
      workMinutes: summaryData.shift?.workMinutes || 0,
      breakMinutes: summaryData.shift?.breakMinutes || 0,
      durationHours: summaryData.shift?.durationHours || 0,
      ...summaryData.shift,
    },
    performance: {
      tablesServed: summaryData.performance?.tablesServed || 0,
      guestsServed: summaryData.performance?.guestsServed || 0,
      salesTotalCents: summaryData.performance?.salesTotalCents || 0,
      tipsTotalCents: summaryData.performance?.tipsTotalCents || 0,
      averageTipPercent: summaryData.performance?.averageTipPercent || 0,
      averageCheckCents: summaryData.performance?.averageCheckCents || 0,
      ...summaryData.performance,
    },
    comparison: {
      salesVsAverage: summaryData.comparison?.salesVsAverage || 0,
      tipsVsAverage: summaryData.comparison?.tipsVsAverage || 0,
      avgSalesCents: summaryData.comparison?.avgSalesCents || 0,
      avgTipsCents: summaryData.comparison?.avgTipsCents || 0,
      ...summaryData.comparison,
    },
    earnings: {
      hourlyRateCents: summaryData.earnings?.hourlyRateCents || 0,
      estimatedWagesCents: summaryData.earnings?.estimatedWagesCents || 0,
      tipsCents: summaryData.earnings?.tipsCents || 0,
      estimatedTotalCents: summaryData.earnings?.estimatedTotalCents || 0,
      ...summaryData.earnings,
    },
    tableBreakdown: summaryData.tableBreakdown || [],
    bestTable: summaryData.bestTable || null,
    ...summaryData,
  } : null;

  return (
    <div className="h-full bg-slate-100 flex flex-col overflow-hidden">
      {/* Header */}
      <header className="bg-primary-900 text-white px-4 py-3 flex items-center justify-between safe-top flex-shrink-0">
        <div className="flex items-center gap-2">
          <LogOut className="w-6 h-6" />
          <span className="font-semibold">End of Shift</span>
        </div>
        <div className="text-sm text-primary-200">
          Step {currentStepIndex + 1} of {STEPS.length}
        </div>
      </header>

      {/* Progress Steps */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          {STEPS.map((step, index) => {
            const isActive = step.id === currentStep;
            const isComplete = index < currentStepIndex;

            return (
              <button
                key={step.id}
                onClick={() => index <= currentStepIndex && setCurrentStep(step.id)}
                disabled={index > currentStepIndex}
                className={clsx(
                  'flex items-center gap-2 transition-all',
                  index > currentStepIndex && 'opacity-40'
                )}
              >
                <div
                  className={clsx(
                    'w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold',
                    isActive && 'bg-primary-600 text-white',
                    isComplete && 'bg-emerald-500 text-white',
                    !isActive && !isComplete && 'bg-slate-200 text-slate-500'
                  )}
                >
                  {isComplete ? <Check className="w-4 h-4" /> : index + 1}
                </div>
                <span className={clsx(
                  'text-sm font-medium hidden sm:block',
                  isActive ? 'text-slate-900' : 'text-slate-500'
                )}>
                  {step.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Content — key={currentStep} remounts the scroll area on each step so
          its scroll position resets to top (otherwise a shorter step like
          Table Breakdown inherits the previous step's scroll and clips at top). */}
      <div ref={contentRef} key={currentStep} className="flex-1 overflow-y-auto p-4 min-h-0 scroll-bounce" style={{ WebkitOverflowScrolling: 'touch' }}>
        {/* SUMMARY STEP */}
        {currentStep === 'summary' && summary && (
          <div className="space-y-4">
            {/* Performance Comparison Banner */}
            {(summary.comparison.salesVsAverage !== 0 || summary.comparison.tipsVsAverage !== 0) && (
              <div className={`rounded-2xl p-4 ${
                summary.comparison.salesVsAverage >= 0 && summary.comparison.tipsVsAverage >= 0
                  ? 'bg-gradient-to-r from-emerald-500 to-emerald-600'
                  : summary.comparison.salesVsAverage >= 10 || summary.comparison.tipsVsAverage >= 10
                    ? 'bg-gradient-to-r from-emerald-500 to-emerald-600'
                    : 'bg-gradient-to-r from-slate-500 to-slate-600'
              } text-white shadow-lg`}>
                <div className="flex items-center gap-3">
                  {summary.comparison.salesVsAverage >= 0 ? (
                    <TrendingUp className="w-8 h-8" />
                  ) : (
                    <TrendingDown className="w-8 h-8" />
                  )}
                  <div>
                    <p className="font-bold text-lg">
                      {summary.comparison.salesVsAverage >= 10
                        ? `Great shift! You're up ${summary.comparison.salesVsAverage}% vs your average!`
                        : summary.comparison.salesVsAverage >= 0
                          ? `Nice work! Sales at ${summary.comparison.salesVsAverage}% above average`
                          : summary.comparison.salesVsAverage >= -10
                            ? `Slightly below average today (${summary.comparison.salesVsAverage}%)`
                            : `Tough shift - ${Math.abs(summary.comparison.salesVsAverage)}% below your usual`
                      }
                    </p>
                    <p className="text-sm opacity-90">
                      30-day average: {formatCurrency(summary.comparison.avgSalesCents)} sales, {formatCurrency(summary.comparison.avgTipsCents)} tips
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Shift Duration Card */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                  <Clock className="w-6 h-6 text-primary-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Shift Duration</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {formatDuration(summary.shift.workMinutes)}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Clock In</p>
                  <p className="font-medium">
                    {new Date(summary.shift.clockIn).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
                <div>
                  <p className="text-slate-500">Break Time</p>
                  <p className="font-medium">{summary.shift.breakMinutes}m</p>
                </div>
              </div>
            </div>

            {/* Performance Stats */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-4">Your Performance</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="w-4 h-4 text-slate-500" />
                    <span className="text-sm text-slate-500">Tables</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-900">
                    {summary.performance.tablesServed}
                  </p>
                  <p className="text-xs text-slate-500">
                    {summary.performance.guestsServed} guests
                  </p>
                </div>

                <div className="bg-slate-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <DollarSign className="w-4 h-4 text-slate-500" />
                    <span className="text-sm text-slate-500">Sales</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-900">
                    {formatCurrency(summary.performance.salesTotalCents)}
                  </p>
                  <div className="flex items-center gap-1 text-xs">
                    {summary.comparison.salesVsAverage >= 0 ? (
                      <>
                        <TrendingUp className="w-3 h-3 text-emerald-500" />
                        <span className="text-emerald-600">+{summary.comparison.salesVsAverage}%</span>
                      </>
                    ) : (
                      <>
                        <TrendingDown className="w-3 h-3 text-red-500" />
                        <span className="text-red-600">{summary.comparison.salesVsAverage}%</span>
                      </>
                    )}
                    <span className="text-slate-400">vs avg</span>
                  </div>
                </div>

                <div className="bg-emerald-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Star className="w-4 h-4 text-emerald-600" />
                    <span className="text-sm text-emerald-700">Tips</span>
                  </div>
                  <p className="text-2xl font-bold text-emerald-700">
                    {formatCurrency(summary.performance.tipsTotalCents)}
                  </p>
                  <p className="text-xs text-emerald-600">
                    {summary.performance.averageTipPercent}% average
                  </p>
                </div>

                <div className="bg-slate-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <DollarSign className="w-4 h-4 text-slate-500" />
                    <span className="text-sm text-slate-500">Avg Check</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-900">
                    {formatCurrency(summary.performance.averageCheckCents)}
                  </p>
                </div>
              </div>
            </div>

            {/* Earnings Estimate */}
            <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-2xl p-6 text-white shadow-sm">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Estimated Earnings
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-emerald-100">Wages ({summary.shift.durationHours}h @ {formatCurrency(summary.earnings.hourlyRateCents)}/hr)</span>
                  <span className="font-medium">{formatCurrency(summary.earnings.estimatedWagesCents)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-emerald-100">Tips</span>
                  <span className="font-medium">{formatCurrency(summary.earnings.tipsCents)}</span>
                </div>
                <div className="border-t border-emerald-400 pt-2 mt-2 flex justify-between">
                  <span className="font-semibold">Total</span>
                  <span className="text-2xl font-bold">{formatCurrency(summary.earnings.estimatedTotalCents)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TABLES STEP */}
        {currentStep === 'tables' && summary && (
          <div className="space-y-4">
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-4">Table Breakdown</h3>

              {summary.tableBreakdown.length > 0 ? (
                <div className="space-y-3">
                  {summary.tableBreakdown.map((table: any, index: number) => (
                    <div
                      key={table.checkId}
                      className={clsx(
                        'p-4 rounded-xl border-2',
                        summary.bestTable?.checkId === table.checkId
                          ? 'border-amber-300 bg-amber-50'
                          : 'border-slate-100 bg-slate-50'
                      )}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-slate-900">
                              Table {table.tableNumber || `#${table.checkNumber}`}
                            </span>
                            {summary.bestTable?.checkId === table.checkId && (
                              <span className="px-2 py-0.5 bg-amber-200 text-amber-800 rounded-full text-xs font-semibold flex items-center gap-1">
                                <Star className="w-3 h-3" />
                                Best
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-500">
                            Party of {table.partySize} · {formatDuration(table.durationMinutes)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-slate-900">{formatCurrency(table.totalCents)}</p>
                          <p className="text-sm text-emerald-600">
                            +{formatCurrency(table.tipCents)} tip
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No tables closed during this shift</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* TIPS DECLARATION STEP */}
        {currentStep === 'tips' && summary && (
          <div className="space-y-4">
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-2">Tip Declaration</h3>
              <p className="text-slate-500 text-sm mb-6">
                Please declare any cash tips received during your shift.
              </p>

              {/* Credit Card Tips (from system) */}
              <div className="bg-emerald-50 rounded-xl p-4 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-emerald-700 font-medium">Credit Card Tips</span>
                  <span className="text-2xl font-bold text-emerald-700">
                    {formatCurrency(summary.performance.tipsTotalCents)}
                  </span>
                </div>
                <p className="text-sm text-emerald-600">
                  Automatically calculated from closed checks
                </p>
              </div>

              {/* Cash Tips Input */}
              <div className="mb-6">
                <label className="text-sm font-medium text-slate-700 block mb-2">
                  Cash Tips Received
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 text-lg">$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={cashTipsDeclared || ''}
                    onChange={(e) => setCashTipsDeclared(parseFloat(e.target.value) || 0)}
                    placeholder="0.00"
                    className="w-full pl-8 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-xl text-2xl font-bold focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Enter $0 if you received no cash tips
                </p>
              </div>

              {/* Total Tips Summary */}
              <div className="bg-slate-100 rounded-xl p-4">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-slate-700">Total Tips Declared</span>
                  <span className="text-2xl font-bold text-slate-900">
                    {formatCurrency(summary.performance.tipsTotalCents + Math.round(cashTipsDeclared * 100))}
                  </span>
                </div>
              </div>
            </div>

            {/* Tip Comparison */}
            {summary.comparison.avgTipsCents > 0 && (
              <div className={`rounded-2xl p-4 ${
                summary.performance.tipsTotalCents + Math.round(cashTipsDeclared * 100) >= summary.comparison.avgTipsCents
                  ? 'bg-emerald-50 border border-emerald-200'
                  : 'bg-amber-50 border border-amber-200'
              }`}>
                {summary.performance.tipsTotalCents + Math.round(cashTipsDeclared * 100) >= summary.comparison.avgTipsCents ? (
                  <div className="flex items-center gap-3">
                    <TrendingUp className="w-6 h-6 text-emerald-600" />
                    <div>
                      <p className="font-semibold text-emerald-800">Great job!</p>
                      <p className="text-sm text-emerald-700">
                        Your tips are above your 30-day average of {formatCurrency(summary.comparison.avgTipsCents)}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <TrendingDown className="w-6 h-6 text-amber-600" />
                    <div>
                      <p className="font-semibold text-amber-800">Below average today</p>
                      <p className="text-sm text-amber-700">
                        Your 30-day average is {formatCurrency(summary.comparison.avgTipsCents)}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* NOTES STEP */}
        {currentStep === 'notes' && (
          <div className="space-y-4">
            {/* Notes */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-slate-500" />
                Shift Notes
              </h3>
              <textarea
                value={postShiftNotes}
                onChange={(e) => setPostShiftNotes(e.target.value)}
                placeholder="Any notes for the next shift or management? (optional)"
                className="w-full h-32 p-4 bg-slate-50 border border-slate-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            {/* Issues */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Report Issues
              </h3>

              {/* Existing Issues */}
              {postShiftIssues.length > 0 && (
                <div className="space-y-2 mb-4">
                  {postShiftIssues.map((issue, index) => {
                    const category = ISSUE_CATEGORIES.find(c => c.id === issue.category);
                    const Icon = category?.icon || HelpCircle;

                    return (
                      <div
                        key={index}
                        className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl"
                      >
                        <Icon className="w-5 h-5 text-slate-500 mt-0.5" />
                        <div className="flex-1">
                          <p className="font-medium text-slate-900 capitalize">{issue.category}</p>
                          <p className="text-sm text-slate-600">{issue.description}</p>
                          <span className={clsx(
                            'text-xs font-medium px-2 py-0.5 rounded-full inline-block mt-1',
                            issue.severity === 'high' && 'bg-red-100 text-red-700',
                            issue.severity === 'medium' && 'bg-amber-100 text-amber-700',
                            issue.severity === 'low' && 'bg-slate-100 text-slate-600'
                          )}>
                            {issue.severity}
                          </span>
                        </div>
                        <button
                          onClick={() => removePostShiftIssue(index)}
                          className="p-1 text-slate-400 hover:text-red-500"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Add Issue Form */}
              {showIssueForm ? (
                <div className="space-y-3 p-4 bg-slate-50 rounded-xl">
                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-2">Category</label>
                    <div className="grid grid-cols-4 gap-2">
                      {ISSUE_CATEGORIES.map((cat) => {
                        const Icon = cat.icon;
                        return (
                          <button
                            key={cat.id}
                            onClick={() => setNewIssue({ ...newIssue, category: cat.id })}
                            className={clsx(
                              'p-2 rounded-lg border-2 transition-all flex flex-col items-center gap-1',
                              newIssue.category === cat.id
                                ? 'border-primary-500 bg-primary-50'
                                : 'border-slate-200 hover:border-slate-300'
                            )}
                          >
                            <Icon className="w-5 h-5 text-slate-600" />
                            <span className="text-xs text-slate-600">{cat.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-2">Description</label>
                    <textarea
                      value={newIssue.description}
                      onChange={(e) => setNewIssue({ ...newIssue, description: e.target.value })}
                      placeholder="Describe the issue..."
                      className="w-full h-20 p-3 bg-white border border-slate-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-2">Severity</label>
                    <div className="flex gap-2">
                      {['low', 'medium', 'high'].map((sev) => (
                        <button
                          key={sev}
                          onClick={() => setNewIssue({ ...newIssue, severity: sev })}
                          className={clsx(
                            'flex-1 py-2 rounded-lg border-2 text-sm font-medium capitalize transition-all',
                            newIssue.severity === sev
                              ? 'border-primary-500 bg-primary-50 text-primary-700'
                              : 'border-slate-200 text-slate-600 hover:border-slate-300'
                          )}
                        >
                          {sev}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowIssueForm(false)}
                      className="flex-1 py-2 bg-slate-200 text-slate-700 rounded-lg font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAddIssue}
                      disabled={!newIssue.category || !newIssue.description.trim()}
                      className="flex-1 py-2 bg-primary-600 text-white rounded-lg font-medium disabled:opacity-50"
                    >
                      Add Issue
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowIssueForm(true)}
                  className="w-full py-3 border-2 border-dashed border-slate-300 rounded-xl text-slate-500 hover:border-primary-400 hover:text-primary-600 transition-colors flex items-center justify-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Report an Issue
                </button>
              )}
            </div>
          </div>
        )}

        {/* CLOCK OUT STEP */}
        {currentStep === 'clockout' && summary && (
          <div className="space-y-4">
            <div className="bg-white rounded-2xl p-6 shadow-sm text-center">
              <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <LogOut className="w-10 h-10 text-slate-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Ready to Clock Out?</h2>
              <p className="text-slate-500 mb-6">
                Review your shift summary before completing
              </p>

              {/* Final Summary */}
              <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left">
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Duration</span>
                    <span className="font-semibold">{formatDuration(summary.shift.workMinutes)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Tables Served</span>
                    <span className="font-semibold">{summary.performance.tablesServed}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Total Sales</span>
                    <span className="font-semibold">{formatCurrency(summary.performance.salesTotalCents)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Total Tips</span>
                    <span className="font-semibold text-emerald-600">{formatCurrency(summary.performance.tipsTotalCents)}</span>
                  </div>
                  <div className="border-t border-slate-200 pt-3 flex justify-between">
                    <span className="font-semibold text-slate-900">Estimated Earnings</span>
                    <span className="font-bold text-lg text-emerald-600">
                      {formatCurrency(summary.earnings.estimatedTotalCents)}
                    </span>
                  </div>
                </div>
              </div>

              {postShiftNotes && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4 text-left">
                  <p className="text-xs text-blue-600 font-medium mb-1">Your Notes:</p>
                  <p className="text-sm text-blue-800">{postShiftNotes}</p>
                </div>
              )}

              {postShiftIssues.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 text-left">
                  <p className="text-xs text-amber-600 font-medium mb-1">Issues Reported:</p>
                  <p className="text-sm text-amber-800">{postShiftIssues.length} issue(s) will be submitted</p>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 text-red-700 text-sm">
                  {error}
                </div>
              )}

              <button
                onClick={() => clockOutMutation.mutate()}
                disabled={clockOutMutation.isPending}
                className="w-full py-4 bg-red-500 text-white rounded-xl font-semibold text-lg hover:bg-red-600 transition-colors disabled:opacity-50"
              >
                {clockOutMutation.isPending ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Clocking Out...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <LogOut className="w-5 h-5" />
                    Clock Out & Complete
                  </span>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="px-4 pb-4 flex gap-3 bg-slate-100 flex-shrink-0 safe-bottom">
        {currentStepIndex > 0 && (
          <button
            onClick={handleBack}
            className="flex-1 py-3 bg-white border border-slate-200 text-slate-700 rounded-xl font-semibold flex items-center justify-center gap-2"
          >
            <ChevronLeft className="w-5 h-5" />
            Back
          </button>
        )}
        {currentStepIndex < STEPS.length - 1 && (
          <button
            onClick={handleNext}
            className="flex-1 py-3 bg-primary-600 text-white rounded-xl font-semibold flex items-center justify-center gap-2"
          >
            Next
            <ChevronRight className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );
}
